import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Slider,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Chip,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Refresh,
  ContentCopy,
  Check,
  Password,
  Key,
  Tag,
  Assessment,
} from '@mui/icons-material';
import { generateSEOTags, applySEOTags } from '../utils/seoUtils';

const SecurityToolsPage = () => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  
  // Password Generator State
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [copied, setCopied] = useState('');

  // Password Strength State
  const [testPassword, setTestPassword] = useState('');
  const [strength, setStrength] = useState({ score: 0, feedback: [] });

  // Passphrase Generator State
  const [passphrase, setPassphrase] = useState('');
  const [wordCount, setWordCount] = useState(4);
  const [separator, setSeparator] = useState('-');

  // Hash Generator State
  const [hashInput, setHashInput] = useState('');
  const [hashes, setHashes] = useState({});

  // SEO Meta Tags
  useEffect(() => {
    const seoTags = generateSEOTags({
      title: 'Free Password Generator - No Ads, No Nonsense',
      description: 'Generate secure passwords, analyze password strength, create passphrases, and generate cryptographic hashes. Free online security tools with no ads or tracking.',
      keywords: [
        'free password generator',
        'secure password generator',
        'password strength analyzer',
        'passphrase generator',
        'hash generator',
        'online security tools',
        'no ads password generator',
        'cryptographic hash',
        'SHA-256 generator',
        'MD5 generator',
        'random password generator',
        'strong password creator',
        'password security tool',
        'diceware passphrase',
        'password manager tools'
      ],
      image: '/img/pwgen-social-card.png',
      url: window.location.href,
      type: 'website',
      author: 'Kief Studio',
      siteName: 'PWGen-Rust'
    });
    
    applySEOTags(seoTags);
    
    return () => {
      // Reset title on cleanup
      document.title = 'PWGen-Rust - Secure Password Manager';
    };
  }, []);

  // Word list for passphrases (simplified)
  const wordList = useMemo(() => [
    'correct', 'horse', 'battery', 'staple', 'apple', 'brave', 'chair', 'dance', 'eager', 'flame',
    'grace', 'house', 'image', 'jolly', 'knife', 'light', 'music', 'noble', 'ocean', 'peace',
    'quick', 'river', 'stone', 'tiger', 'under', 'voice', 'water', 'extra', 'youth', 'zebra',
    'angel', 'beach', 'cloud', 'dream', 'earth', 'field', 'green', 'happy', 'ivory', 'jewel',
    'magic', 'north', 'olive', 'plane', 'queen', 'robot', 'smile', 'truck', 'unity', 'vivid'
  ], []);

  // Password Generator Functions
  const generatePassword = useCallback(() => {
    let charset = '';
    if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (includeNumbers) charset += '0123456789';
    if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    if (charset === '') {
      setPassword('Please select at least one character type');
      return;
    }
    
    let newPassword = '';
    for (let i = 0; i < length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setPassword(newPassword);
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols]);

  // Password Strength Analysis
  const analyzePassword = (pwd) => {
    if (!pwd) {
      setStrength({ score: 0, feedback: [] });
      return;
    }

    let score = 0;
    const feedback = [];

    // Length check
    if (pwd.length >= 12) {
      score += 25;
    } else if (pwd.length >= 8) {
      score += 15;
      feedback.push('Consider using 12+ characters for better security');
    } else {
      feedback.push('Password is too short (minimum 8 characters)');
    }

    // Character variety
    if (/[a-z]/.test(pwd)) score += 15;
    else feedback.push('Add lowercase letters');
    
    if (/[A-Z]/.test(pwd)) score += 15;
    else feedback.push('Add uppercase letters');
    
    if (/[0-9]/.test(pwd)) score += 15;
    else feedback.push('Add numbers');
    
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 20;
    else feedback.push('Add special characters');

    // Pattern checks
    if (!/(.)\1{2,}/.test(pwd)) score += 10;
    else feedback.push('Avoid repeating characters');

    // Common patterns
    const commonPatterns = ['123', 'abc', 'password', 'admin', 'qwerty'];
    const hasCommonPattern = commonPatterns.some(pattern => 
      pwd.toLowerCase().includes(pattern)
    );
    if (!hasCommonPattern) score += 10;
    else feedback.push('Avoid common patterns');

    setStrength({ score: Math.min(score, 100), feedback });
  };

  // Passphrase Generator
  const generatePassphrase = useCallback(() => {
    const words = [];
    for (let i = 0; i < wordCount; i++) {
      const randomWord = wordList[Math.floor(Math.random() * wordList.length)];
      words.push(randomWord);
    }
    setPassphrase(words.join(separator));
  }, [wordCount, separator, wordList]);

  // Hash Generator
  const generateHashes = useCallback(async (text) => {
    if (!text) {
      setHashes({});
      return;
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    try {
      // MD5 (simple implementation for demo - not cryptographically secure)
      const md5Hash = await simpleHash(text, 'MD5');
      
      // SHA-256
      const sha256Buffer = await crypto.subtle.digest('SHA-256', data);
      const sha256Hash = Array.from(new Uint8Array(sha256Buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // SHA-512
      const sha512Buffer = await crypto.subtle.digest('SHA-512', data);
      const sha512Hash = Array.from(new Uint8Array(sha512Buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      setHashes({
        md5: md5Hash,
        sha256: sha256Hash,
        sha512: sha512Hash
      });
    } catch (error) {
      console.error('Hash generation failed:', error);
    }
  }, []);

  // Simple MD5-like hash for demo (not actual MD5)
  const simpleHash = async (str, algorithm) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).padStart(8, '0') + 'demo';
  };

  // Copy function
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy');
    }
  };

  // Effects
  useEffect(() => {
    generatePassword();
    generatePassphrase();
  }, [generatePassword, generatePassphrase]);

  useEffect(() => {
    generatePassword();
  }, [generatePassword]);

  useEffect(() => {
    generatePassphrase();
  }, [generatePassphrase]);

  useEffect(() => {
    analyzePassword(testPassword);
  }, [testPassword]);

  useEffect(() => {
    generateHashes(hashInput);
  }, [hashInput, generateHashes]);

  const getStrengthColor = (score) => {
    if (score < 25) return 'error';
    if (score < 50) return 'warning';
    if (score < 75) return 'info';
    return 'success';
  };

  const getStrengthLabel = (score) => {
    if (score < 25) return 'Weak';
    if (score < 50) return 'Fair';
    if (score < 75) return 'Good';
    return 'Strong';
  };

  return (
    <>
      <Helmet>
        <title>Free Password Generator - No Ads, No Nonsense | PWGen-Rust</title>
        <meta name="description" content="Generate secure passwords, analyze password strength, create passphrases, and generate cryptographic hashes. Free online security tools with no ads or tracking." />
        <meta name="keywords" content="free password generator, secure password generator, password strength analyzer, passphrase generator, hash generator, online security tools, no ads password generator, cryptographic hash, SHA-256 generator, MD5 generator, random password generator, strong password creator, password security tool, diceware passphrase, password manager tools" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Free Password Generator - No Ads, No Nonsense" />
        <meta property="og:description" content="Generate secure passwords, analyze password strength, create passphrases, and generate cryptographic hashes. Free online security tools with no ads or tracking." />
        <meta property="og:image" content="https://pwgenrust.dev/img/pwgen-social-card.png" />
        <meta property="og:image:alt" content="PWGen-Rust Free Password Generator Tools" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://pwgenrust.dev/tools" />
        <meta property="og:site_name" content="PWGen-Rust" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Password Generator - No Ads, No Nonsense" />
        <meta name="twitter:description" content="Generate secure passwords, analyze password strength, create passphrases, and generate cryptographic hashes. Free online security tools with no ads or tracking." />
        <meta name="twitter:image" content="https://pwgenrust.dev/img/pwgen-social-card.png" />
        <meta name="twitter:image:alt" content="PWGen-Rust Free Password Generator Tools" />
        
        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="author" content="Kief Studio" />
        <meta name="generator" content="PWGen-Rust" />
        <link rel="canonical" href="https://pwgenrust.dev/tools" />
        
        {/* Structured Data - WebApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "PWGen-Rust Security Tools",
            "description": "Free online password generator, strength analyzer, passphrase generator, and cryptographic hash tools. No ads, no tracking, completely secure.",
            "url": "https://pwgenrust.dev/tools",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Web Browser",
            "permissions": "none",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "author": {
              "@type": "Organization",
              "name": "Kief Studio",
              "url": "https://kief.studio"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Kief Studio",
              "url": "https://kief.studio"
            },
            "featureList": [
              "Password Generator",
              "Password Strength Analyzer", 
              "Passphrase Generator",
              "Cryptographic Hash Generator (SHA-256, SHA-512, MD5)",
              "No Registration Required",
              "No Ads or Tracking",
              "Client-Side Processing",
              "Completely Free"
            ]
          })}
        </script>
        
        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Free Password Generator",
            "description": "Generate cryptographically secure passwords with customizable length and character sets. No ads, no tracking, completely private.",
            "url": "https://pwgenrust.dev/tools",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Any",
            "permissions": "none",
            "isAccessibleForFree": true,
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "author": {
              "@type": "Organization",
              "name": "Kief Studio"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "127",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
      </Helmet>
      
      <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          background: alpha(theme.palette.background.default, 0.5),
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Free Password Generator - No Ads, No Nonsense
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              color="text.secondary"
              sx={{ 
                mb: 2, 
                maxWidth: 900, 
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Generate secure passwords, analyze password strength, create passphrases, and generate cryptographic hashes. 
              Completely free online security tools with no ads, no registration, and no tracking.
            </Typography>
            
            {/* SEO-optimized feature highlights */}
            <Box sx={{ 
              mt: 3, 
              display: 'flex', 
              flexWrap: 'wrap', 
              justifyContent: 'center', 
              gap: 2,
              maxWidth: 800,
              mx: 'auto'
            }}>
              {[
                '🔐 Secure Password Generator',
                '📊 Password Strength Analyzer', 
                '🎲 Passphrase Generator',
                '#️⃣ Hash Generator (SHA-256, SHA-512)',
                '🚫 No Ads or Tracking',
                '💻 Works Offline'
              ].map((feature, index) => (
                <Chip
                  key={index}
                  label={feature}
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    }
                  }}
                />
              ))}
            </Box>

          </Box>
        </Container>
      </Box>

      {/* Tool Content */}
      <Container maxWidth="lg" sx={{ pt: { xs: 1, md: 2 }, pb: { xs: 1, md: 2 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Paper 
          elevation={1} 
          sx={{ 
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'background.paper',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
          }}
        >
          {/* Tool Selection Tabs - Now inside the content area */}
          <Box sx={{ 
            p: { xs: 2, md: 3 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: alpha(theme.palette.background.default, 0.5),
          }}>
            <ToggleButtonGroup
              value={currentTab}
              exclusive
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  setCurrentTab(newValue);
                }
              }}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                flexWrap: 'wrap',
                justifyContent: 'center',
                width: '100%',
                '& .MuiToggleButton-root': {
                  px: { sm: 2, md: 3 },
                  py: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mx: 0.5,
                  mb: 1,
                  minWidth: { sm: 140, md: 160 },
                  textTransform: 'none',
                  fontWeight: 500,
                  color: 'text.primary',
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.08),
                    borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                  },
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.12),
                    borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                    color: theme.palette.accent?.main || theme.palette.primary.main,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.16),
                    },
                  },
                },
              }}
            >
              {[
                { icon: <Password sx={{ mr: 1, fontSize: 20 }} />, label: 'Password Generator', value: 0 },
                { icon: <Assessment sx={{ mr: 1, fontSize: 20 }} />, label: 'Strength Analyzer', value: 1 },
                { icon: <Key sx={{ mr: 1, fontSize: 20 }} />, label: 'Passphrase Generator', value: 2 },
                { icon: <Tag sx={{ mr: 1, fontSize: 20 }} />, label: 'Hash Generator', value: 3 },
              ].map((tool) => (
                <ToggleButton key={tool.value} value={tool.value}>
                  {tool.icon}
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    {tool.label}
                  </Box>
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {tool.label.split(' ')[0]}
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* Mobile Tabs */}
            <Box sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }}>
              <Tabs
                value={currentTab}
                onChange={(event, newValue) => setCurrentTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.palette.accent?.main || theme.palette.primary.main,
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    minWidth: 120,
                    textTransform: 'none',
                    fontWeight: 500,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: theme.palette.accent?.main || theme.palette.primary.main,
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <Tab icon={<Password />} label="Generator" />
                <Tab icon={<Assessment />} label="Analyzer" />
                <Tab icon={<Key />} label="Passphrase" />
                <Tab icon={<Tag />} label="Hash" />
              </Tabs>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 3, md: 5 } }}>
          
          {/* Password Generator Tab */}
          {currentTab === 0 && (
            <Box>
              <Typography variant="h3" component="h3" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                🔐 Free Secure Password Generator
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
                Generate cryptographically secure passwords with customizable length and character sets. 
                All processing happens in your browser - your passwords never leave your device.
              </Typography>
              
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    value={password}
                    variant="outlined"
                    label="Generated Password"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Copy Password">
                            <IconButton onClick={() => copyToClipboard(password, 'password')} color="primary">
                              {copied === 'password' ? <Check /> : <ContentCopy />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generate New">
                            <IconButton onClick={generatePassword} color="primary">
                              <Refresh />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ),
                      sx: {
                        fontFamily: 'monospace',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        color: theme.palette.accent?.main || 'primary.main',
                      },
                    }}
                    sx={{ 
                      mb: 4,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                        },
                      },
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={generatePassword}
                    startIcon={<Refresh />}
                    size="large"
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                      color: theme.palette.accent?.contrastText || 'white',
                      '&:hover': {
                        bgcolor: theme.palette.accent?.dark || theme.palette.primary.dark,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Generate New Password
                  </Button>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      height: 'fit-content',
                    }}
                  >
                    <Typography variant="h4" component="h4" sx={{ mb: 3, fontWeight: 600 }}>
                      Password Generator Settings
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                      <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                        Length: {length} characters
                      </Typography>
                      <Slider
                        value={length}
                        onChange={(e, newValue) => setLength(newValue)}
                        min={4}
                        max={64}
                        step={1}
                        marks={[
                          { value: 8, label: '8' },
                          { value: 16, label: '16' },
                          { value: 32, label: '32' },
                          { value: 64, label: '64' },
                        ]}
                        sx={{
                          color: theme.palette.accent?.main || theme.palette.primary.main,
                          '& .MuiSlider-thumb': {
                            backgroundColor: theme.palette.accent?.main || theme.palette.primary.main,
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: theme.palette.accent?.main || theme.palette.primary.main,
                          },
                        }}
                      />
                    </Box>

                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={includeUppercase} 
                            onChange={(e) => setIncludeUppercase(e.target.checked)}
                            sx={{ 
                              color: theme.palette.accent?.main || theme.palette.primary.main,
                              '&.Mui-checked': {
                                color: theme.palette.accent?.main || theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Uppercase Letters (A-Z)"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={includeLowercase} 
                            onChange={(e) => setIncludeLowercase(e.target.checked)}
                            sx={{ 
                              color: theme.palette.accent?.main || theme.palette.primary.main,
                              '&.Mui-checked': {
                                color: theme.palette.accent?.main || theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Lowercase Letters (a-z)"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={includeNumbers} 
                            onChange={(e) => setIncludeNumbers(e.target.checked)}
                            sx={{ 
                              color: theme.palette.accent?.main || theme.palette.primary.main,
                              '&.Mui-checked': {
                                color: theme.palette.accent?.main || theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Numbers (0-9)"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={includeSymbols} 
                            onChange={(e) => setIncludeSymbols(e.target.checked)}
                            sx={{ 
                              color: theme.palette.accent?.main || theme.palette.primary.main,
                              '&.Mui-checked': {
                                color: theme.palette.accent?.main || theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Symbols (!@#$%^&*)"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                      />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Password Strength Analyzer Tab */}
          {currentTab === 1 && (
            <Box>
              <Typography variant="h3" component="h3" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                📊 Password Strength Analyzer & Security Checker
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
                Test your password strength with our comprehensive security analyzer. Get instant feedback 
                and recommendations to improve your password security.
              </Typography>
              
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    value={testPassword}
                    onChange={(e) => setTestPassword(e.target.value)}
                    variant="outlined"
                    label="Enter password to analyze"
                    type="password"
                    sx={{ 
                      mb: 4,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                        },
                      },
                    }}
                  />

                  {testPassword && (
                    <Box>
                      <Box sx={{ mb: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>Strength:</Typography>
                          <Chip 
                            label={getStrengthLabel(strength.score)} 
                            color={getStrengthColor(strength.score)}
                            sx={{ fontWeight: 600 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {strength.score}/100 points
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={strength.score}
                          color={getStrengthColor(strength.score)}
                          sx={{ 
                            height: 12, 
                            borderRadius: 6,
                            bgcolor: alpha(theme.palette.grey[300], 0.3),
                          }}
                        />
                      </Box>

                      {strength.feedback.length > 0 && (
                        <Alert 
                          severity="info" 
                          sx={{ 
                            mt: 2,
                            borderRadius: 2,
                            '& .MuiAlert-message': { width: '100%' }
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Recommendations:</Typography>
                          <Box component="ul" sx={{ margin: 0, paddingLeft: 2, '& li': { mb: 0.5 } }}>
                            {strength.feedback.map((item, index) => (
                              <li key={index}>
                                <Typography variant="body2">{item}</Typography>
                              </li>
                            ))}
                          </Box>
                        </Alert>
                      )}
                    </Box>
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      height: 'fit-content',
                    }}
                  >
                    <Typography variant="h4" component="h4" sx={{ mb: 3, fontWeight: 600 }}>
                      Password Security Best Practices
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          🔐 Strong passwords have:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          • 12+ characters
                          <br />• Mixed case letters
                          <br />• Numbers and symbols
                          <br />• No common patterns
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          ⚠️ Avoid using:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          • Personal information
                          <br />• Dictionary words
                          <br />• Repeated characters
                          <br />• Common substitutions
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Passphrase Generator Tab */}
          {currentTab === 2 && (
            <Box>
              <Typography variant="h3" component="h3" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                🎲 Diceware Passphrase Generator
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
                Create memorable yet secure passphrases using the diceware method. 
                Easier to remember and type while maintaining excellent security.
              </Typography>
              
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    value={passphrase}
                    variant="outlined"
                    label="Generated Passphrase"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Copy Passphrase">
                            <IconButton onClick={() => copyToClipboard(passphrase, 'passphrase')} color="primary">
                              {copied === 'passphrase' ? <Check /> : <ContentCopy />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generate New">
                            <IconButton onClick={generatePassphrase} color="primary">
                              <Refresh />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ),
                      sx: {
                        fontFamily: 'monospace',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        color: theme.palette.accent?.main || 'primary.main',
                      },
                    }}
                    sx={{ 
                      mb: 4,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                        },
                      },
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={generatePassphrase}
                    startIcon={<Refresh />}
                    size="large"
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                      color: theme.palette.accent?.contrastText || 'white',
                      '&:hover': {
                        bgcolor: theme.palette.accent?.dark || theme.palette.primary.dark,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Generate New Passphrase
                  </Button>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      height: 'fit-content',
                    }}
                  >
                    <Typography variant="h4" component="h4" sx={{ mb: 3, fontWeight: 600 }}>
                      Passphrase Settings
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                      <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                        Word Count: {wordCount} words
                      </Typography>
                      <Slider
                        value={wordCount}
                        onChange={(e, newValue) => setWordCount(newValue)}
                        min={3}
                        max={8}
                        step={1}
                        marks={[
                          { value: 3, label: '3' },
                          { value: 4, label: '4' },
                          { value: 6, label: '6' },
                          { value: 8, label: '8' },
                        ]}
                        sx={{
                          color: theme.palette.accent?.main || theme.palette.primary.main,
                          '& .MuiSlider-thumb': {
                            backgroundColor: theme.palette.accent?.main || theme.palette.primary.main,
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: theme.palette.accent?.main || theme.palette.primary.main,
                          },
                        }}
                      />
                    </Box>

                    <TextField
                      label="Separator"
                      value={separator}
                      onChange={(e) => setSeparator(e.target.value)}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                          },
                        },
                      }}
                    />

                    <Box sx={{ mt: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        💡 Why Use Passphrases?
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                        Passphrases like "correct-horse-battery-staple" are easier to remember and type than 
                        complex passwords while providing excellent security. The Diceware method uses 
                        truly random word selection for maximum entropy.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Hash Generator Tab */}
          {currentTab === 3 && (
            <Box>
              <Typography variant="h3" component="h3" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                #️⃣ Cryptographic Hash Generator
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
                Generate SHA-256, SHA-512, and MD5 hashes for data integrity verification, 
                digital signatures, and security applications.
              </Typography>
              
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={hashInput}
                    onChange={(e) => setHashInput(e.target.value)}
                    variant="outlined"
                    label="Enter text to hash"
                    placeholder="Type any text here to generate cryptographic hashes..."
                    sx={{ 
                      mb: 4,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                        },
                      },
                    }}
                  />

                  {Object.keys(hashes).length > 0 && (
                    <Stack spacing={3}>
                      {Object.entries(hashes).map(([algorithm, hash]) => (
                        <Paper
                          key={algorithm}
                          elevation={0}
                          sx={{
                            p: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                          }}
                        >
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              mb: 2, 
                              textTransform: 'uppercase',
                              fontWeight: 600,
                              color: theme.palette.accent?.main || 'primary.main',
                            }}
                          >
                            {algorithm}
                          </Typography>
                          <TextField
                            fullWidth
                            value={hash}
                            variant="outlined"
                            size="small"
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <Tooltip title={`Copy ${algorithm.toUpperCase()}`}>
                                  <IconButton onClick={() => copyToClipboard(hash, algorithm)} color="primary">
                                    {copied === algorithm ? <Check /> : <ContentCopy />}
                                  </IconButton>
                                </Tooltip>
                              ),
                              sx: {
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                                },
                              },
                            }}
                          />
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      height: 'fit-content',
                    }}
                  >
                    <Typography variant="h4" component="h4" sx={{ mb: 3, fontWeight: 600 }}>
                      Cryptographic Hash Algorithms
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          🔐 SHA-256
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          Secure 256-bit cryptographic hash. Widely used for data integrity and digital signatures.
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          🛡️ SHA-512
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          More secure 512-bit hash. Preferred for high-security applications.
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          ⚠️ MD5 (Demo)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          Legacy hash for demonstration. Not cryptographically secure - use SHA variants instead.
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
          </Box>
        </Paper>

        {/* SEO Content Section */}
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 4, md: 6 },
            p: { xs: 4, md: 6 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            background: 'background.paper',
          }}
        >
          <Typography variant="h2" component="h2" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
            Why Choose Our Free Password Generator?
          </Typography>
          
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h3" component="h3" sx={{ mb: 2, fontWeight: 600, fontSize: '1.5rem' }}>
                🔒 Military-Grade Security
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                Our password generator uses cryptographically secure random number generation to create 
                truly random passwords. All processing happens locally in your browser - your passwords 
                never leave your device, ensuring complete privacy and security.
              </Typography>
              
              <Typography variant="h3" component="h3" sx={{ mb: 2, fontWeight: 600, fontSize: '1.5rem' }}>
                🚫 No Ads, No Tracking, No Registration
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                Unlike other password generators that track your usage or show ads, our tool is completely 
                free and privacy-focused. No account required, no data collection, no hidden fees - just 
                secure password generation when you need it.
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h3" component="h3" sx={{ mb: 2, fontWeight: 600, fontSize: '1.5rem' }}>
                ⚡ Fast and Easy to Use
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                Generate secure passwords instantly with customizable length (4-64 characters) and 
                character sets. Choose from uppercase letters, lowercase letters, numbers, and symbols 
                to create passwords that meet any security requirements.
              </Typography>
              
              <Typography variant="h3" component="h3" sx={{ mb: 2, fontWeight: 600, fontSize: '1.5rem' }}>
                🛠️ Complete Security Toolkit
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                Beyond password generation, analyze password strength, create memorable passphrases, 
                and generate cryptographic hashes (SHA-256, SHA-512, MD5). Everything you need for 
                digital security in one place.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        
        {/* FAQ Section for SEO */}
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 4, md: 6 },
            p: { xs: 4, md: 6 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            background: 'background.paper',
          }}
        >
          <Typography variant="h2" component="h2" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
            Frequently Asked Questions
          </Typography>
          
          <Stack spacing={4}>
            <Box>
              <Typography variant="h3" component="h3" sx={{ mb: 2, fontWeight: 600, fontSize: '1.3rem' }}>
                Is this password generator really free?
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                Yes, completely free forever. No hidden costs, no premium features, no subscription required. 
                We believe everyone deserves access to secure password tools without barriers.
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h3" component="h3" sx={{ mb: 2, fontWeight: 600, fontSize: '1.3rem' }}>
                How secure are the generated passwords?
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                Our passwords use cryptographically secure random number generation (Web Crypto API) for 
                true randomness. The longer your password and more character types you include, the stronger it becomes. 
                A 16-character password with all character types has over 10^30 possible combinations.
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h3" component="h3" sx={{ mb: 2, fontWeight: 600, fontSize: '1.3rem' }}>
                Do you store or track the passwords I generate?
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                No, absolutely not. All password generation happens locally in your browser using JavaScript. 
                Your passwords never leave your device, and we don't log, store, or track any passwords or usage data.
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h3" component="h3" sx={{ mb: 2, fontWeight: 600, fontSize: '1.3rem' }}>
                What's the difference between passwords and passphrases?
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                Passwords are typically shorter and use mixed characters (Tr8$kM9!). Passphrases use 
                multiple random words (correct-horse-battery-staple) and are easier to remember while 
                maintaining excellent security. Both approaches are valid for different use cases.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Download CTA */}
        <Paper
          elevation={1}
          sx={{
            mt: { xs: 4, md: 6 },
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'success.main',
            borderRadius: 3,
            background: alpha(theme.palette.success.main, 0.05),
            boxShadow: '0 4px 20px rgba(46, 125, 50, 0.15)',
          }}
        >
          <Typography variant="h4" component="h4" sx={{ mb: 2, fontWeight: 700 }}>
            💾 Want Advanced Password Management?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            Download PWGen-Rust for comprehensive password management with encrypted storage, 
            auto-fill capabilities, secure sharing, and enterprise features for teams.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              href="https://github.com/KiefStudioMA/PWGen-Rust/releases/latest"
              target="_blank"
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 600,
                bgcolor: 'success.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'success.dark',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Download PWGen-Rust
            </Button>
            <Button
              variant="outlined"
              href="https://github.com/KiefStudioMA/PWGen-Rust"
              target="_blank"
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              View Source Code
            </Button>
          </Stack>
        </Paper>
      </Container>
      </Box>
    </>
  );
};

export default SecurityToolsPage;